import React, { useState } from 'react'
import {
  Box,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Divider,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Alert,
  Chip,
  InputAdornment,
  IconButton,
} from '@mui/material'
import {
  Security as SecurityIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Shield as ShieldIcon,
  Key as KeyIcon,
  Lock as LockIcon,
  VpnKey as VpnKeyIcon,
} from '@mui/icons-material'

const SecuritySettings = ({ settings = {}, onChange, isLoading = false }) => {
  const [localSettings, setLocalSettings] = useState({
    twoFactorRequired: false,
    passwordMinLength: 8,
    passwordRequireSpecial: true,
    passwordRequireNumbers: true,
    passwordRequireUppercase: true,
    loginAttemptLimit: 5,
    lockoutDuration: 30,
    sessionTimeout: 24,
    ipWhitelist: '',
    enableApiKeyAuth: true,
    requireHttps: true,
    enableCors: false,
    corsOrigins: '',
    jwtSecret: '••••••••••••••••',
    encryptionKey: '••••••••••••••••',
    ...settings
  })

  const [showSecrets, setShowSecrets] = useState({
    jwtSecret: false,
    encryptionKey: false,
  })

  const handleSettingChange = (key, value) => {
    setLocalSettings(prev => ({
      ...prev,
      [key]: value
    }))
    onChange?.()
  }

  const toggleSecretVisibility = (key) => {
    setShowSecrets(prev => ({
      ...prev,
      [key]: !prev[key]
    }))
  }

  const generateSecret = (key) => {
    const newSecret = Array.from(crypto.getRandomValues(new Uint8Array(32)))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('')
    
    handleSettingChange(key, newSecret)
  }

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
          Güvenlik Ayarları
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Sistem güvenliği ve erişim kontrolü ayarları
        </Typography>
      </Box>

      {/* Authentication Settings */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <KeyIcon />
            Kimlik Doğrulama
          </Typography>
          
          <List>
            <ListItem>
              <ListItemText
                primary="İki Faktörlü Kimlik Doğrulama (2FA)"
                secondary="Tüm admin kullanıcılar için 2FA zorunlu olsun"
              />
              <ListItemSecondaryAction>
                <Switch
                  checked={localSettings.twoFactorRequired}
                  onChange={(e) => handleSettingChange('twoFactorRequired', e.target.checked)}
                  color="primary"
                />
              </ListItemSecondaryAction>
            </ListItem>
            
            <Divider />
            
            <ListItem>
              <ListItemText
                primary="HTTPS Zorunluluğu"
                secondary="Tüm bağlantılar için HTTPS protokolü zorunlu olsun"
              />
              <ListItemSecondaryAction>
                <Switch
                  checked={localSettings.requireHttps}
                  onChange={(e) => handleSettingChange('requireHttps', e.target.checked)}
                  color="success"
                />
              </ListItemSecondaryAction>
            </ListItem>
            
            <Divider />
            
            <ListItem>
              <ListItemText
                primary="API Key Authentication"
                secondary="API erişimi için key tabanlı kimlik doğrulama"
              />
              <ListItemSecondaryAction>
                <Switch
                  checked={localSettings.enableApiKeyAuth}
                  onChange={(e) => handleSettingChange('enableApiKeyAuth', e.target.checked)}
                  color="info"
                />
              </ListItemSecondaryAction>
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {/* Password Policy */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <LockIcon />
            Şifre Politikası
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Minimum Şifre Uzunluğu"
                type="number"
                value={localSettings.passwordMinLength}
                onChange={(e) => handleSettingChange('passwordMinLength', parseInt(e.target.value) || 8)}
                helperText="Şifre için minimum karakter sayısı"
                inputProps={{ min: 6, max: 32 }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Oturum Zaman Aşımı (saat)"
                type="number"
                value={localSettings.sessionTimeout}
                onChange={(e) => handleSettingChange('sessionTimeout', parseInt(e.target.value) || 24)}
                helperText="Kullanıcı oturumu zaman aşımı süresi"
                inputProps={{ min: 1, max: 168 }}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Şifre Gereksinimleri
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={localSettings.passwordRequireSpecial}
                      onChange={(e) => handleSettingChange('passwordRequireSpecial', e.target.checked)}
                      size="small"
                    />
                  }
                  label="Özel karakterler (!@#$%)"
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={localSettings.passwordRequireNumbers}
                      onChange={(e) => handleSettingChange('passwordRequireNumbers', e.target.checked)}
                      size="small"
                    />
                  }
                  label="Rakamlar (0-9)"
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={localSettings.passwordRequireUppercase}
                      onChange={(e) => handleSettingChange('passwordRequireUppercase', e.target.checked)}
                      size="small"
                    />
                  }
                  label="Büyük harfler (A-Z)"
                />
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>

      {/* Login Security */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShieldIcon />
            Giriş Güvenliği
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Maksimum Giriş Denemesi"
                type="number"
                value={localSettings.loginAttemptLimit}
                onChange={(e) => handleSettingChange('loginAttemptLimit', parseInt(e.target.value) || 5)}
                helperText="Hesap kilitlenmeden önce maksimum hatalı giriş sayısı"
                inputProps={{ min: 3, max: 20 }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Hesap Kilitleme Süresi (dakika)"
                type="number"
                value={localSettings.lockoutDuration}
                onChange={(e) => handleSettingChange('lockoutDuration', parseInt(e.target.value) || 30)}
                helperText="Hesabın kilitli kalacağı süre"
                inputProps={{ min: 5, max: 1440 }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="IP Beyaz Listesi"
                value={localSettings.ipWhitelist}
                onChange={(e) => handleSettingChange('ipWhitelist', e.target.value)}
                helperText="Admin paneline erişim için izinli IP adresleri (virgül ile ayırın, boş bırakılırsa tüm IP'ler izinli)"
                placeholder="192.168.1.1, 10.0.0.1, 203.0.113.0/24"
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* API Security */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <VpnKeyIcon />
            API Güvenliği
          </Typography>
          
          <List>
            <ListItem>
              <ListItemText
                primary="CORS (Cross-Origin Resource Sharing)"
                secondary="Farklı domain'lerden API erişimini kontrol et"
              />
              <ListItemSecondaryAction>
                <Switch
                  checked={localSettings.enableCors}
                  onChange={(e) => handleSettingChange('enableCors', e.target.checked)}
                  color="warning"
                />
              </ListItemSecondaryAction>
            </ListItem>
          </List>

          {localSettings.enableCors && (
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="İzinli CORS Origin'leri"
                value={localSettings.corsOrigins}
                onChange={(e) => handleSettingChange('corsOrigins', e.target.value)}
                helperText="İzinli domain'ler (virgül ile ayırın)"
                placeholder="https://example.com, https://app.example.com"
                multiline
                rows={2}
              />
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="JWT Secret Key"
                type={showSecrets.jwtSecret ? "text" : "password"}
                value={localSettings.jwtSecret}
                onChange={(e) => handleSettingChange('jwtSecret', e.target.value)}
                helperText="JWT token imzalama için kullanılan gizli anahtar"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => toggleSecretVisibility('jwtSecret')}
                        edge="end"
                      >
                        {showSecrets.jwtSecret ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                size="small"
                onClick={() => generateSecret('jwtSecret')}
                sx={{ mt: 1 }}
              >
                Yeni Anahtar Oluştur
              </Button>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Encryption Key"
                type={showSecrets.encryptionKey ? "text" : "password"}
                value={localSettings.encryptionKey}
                onChange={(e) => handleSettingChange('encryptionKey', e.target.value)}
                helperText="Veri şifreleme için kullanılan anahtar"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => toggleSecretVisibility('encryptionKey')}
                        edge="end"
                      >
                        {showSecrets.encryptionKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                size="small"
                onClick={() => generateSecret('encryptionKey')}
                sx={{ mt: 1 }}
              >
                Yeni Anahtar Oluştur
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Security Warnings */}
      <Box sx={{ mb: 3 }}>
        {!localSettings.requireHttps && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Güvenlik Uyarısı:</strong> HTTPS zorunluluğu devre dışı. Güvenlik açısından HTTPS kullanılması önerilir.
            </Typography>
          </Alert>
        )}

        {localSettings.enableCors && !localSettings.corsOrigins && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Kritik Uyarı:</strong> CORS etkin ancak origin'ler belirtilmemiş. Bu durum güvenlik riski oluşturabilir.
            </Typography>
          </Alert>
        )}

        {localSettings.passwordMinLength < 8 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Şifre Uyarısı:</strong> Minimum şifre uzunluğu 8 karakterden az. Güvenlik için en az 8 karakter önerilir.
            </Typography>
          </Alert>
        )}
      </Box>

      {/* Security Status */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Güvenlik Durumu
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Chip 
                  label={localSettings.twoFactorRequired ? "Etkin" : "Devre Dışı"}
                  color={localSettings.twoFactorRequired ? "success" : "error"}
                  sx={{ mb: 1 }}
                />
                <Typography variant="caption" display="block">
                  2FA Durumu
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Chip 
                  label={localSettings.requireHttps ? "Etkin" : "Devre Dışı"}
                  color={localSettings.requireHttps ? "success" : "warning"}
                  sx={{ mb: 1 }}
                />
                <Typography variant="caption" display="block">
                  HTTPS Zorunluluğu
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Chip 
                  label={localSettings.ipWhitelist ? "Aktif" : "Pasif"}
                  color={localSettings.ipWhitelist ? "info" : "default"}
                  sx={{ mb: 1 }}
                />
                <Typography variant="caption" display="block">
                  IP Kısıtlaması
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Chip 
                  label={`${localSettings.loginAttemptLimit} Deneme`}
                  color="default"
                  sx={{ mb: 1 }}
                />
                <Typography variant="caption" display="block">
                  Giriş Limiti
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  )
}

export default SecuritySettings